import {
  ContextualFeaturePermissionDocument,
  ContextualFeaturePermissionQuery,
  ContextualFeaturePermissionQueryVariables,
  FeatureAction,
  FeatureKey,
  FeaturePermission,
  MeFragment,
  ResourceFragment,
  useContextualFeaturePermissionQuery
} from "@pathwright/graphql-client/src/generated"
import { getApolloClient } from "@pathwright/features/src/modules/pathwright/PathwrightClient"
import { PathwrightContext } from "@pathwright/features/src/modules/pathwright/PathwrightContext"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { canAdministrate, canEditLibrary } from "../user/permissions"
import { useCoverPageContext } from "./CoverPageProvider"

export const isCoverPagesFeatureEnabled = (pwContext: PathwrightContext) => {
  return !!pwContext.school?.features?.library__cover_pages
}

const coverPagesCfpVariables: ContextualFeaturePermissionQueryVariables = {
  featureKey: FeatureKey.LibraryCoverPages,
  featureAction: FeatureAction.CoverPagesView
}

const isCoverPagesLaunched = (cfpPerm?: FeaturePermission | null): boolean => {
  return cfpPerm === FeaturePermission.Everyone
}

const canStaffPreviewFromAbout = (pwContext: PathwrightContext): boolean => {
  return canAdministrate(pwContext) || canEditLibrary(pwContext)
}

export const getCoverPageIsEditable = ({
  me,
  licensorSchoolId,
  resource
}: {
  me: MeFragment | null
  licensorSchoolId?: number | null
  resource:
    | Pick<ResourceFragment, "licensed_from_resource_id">
    | null
    | undefined
}) => {
  return (
    !!me &&
    !licensorSchoolId &&
    !!resource &&
    !resource.licensed_from_resource_id &&
    (me.permissions?.can_edit_library ||
      me.permissions?.can_administrate ||
      me.permissions?.has_pathwright_staff_access)
  )
}

export const useCoverPageIsEditable = () => {
  const { me } = usePathwrightContext()
  const licensorSchoolId = useCoverPageContext(
    (value) => value.licensorSchoolId
  )
  const resource = useCoverPageContext((value) => value.resource)

  return getCoverPageIsEditable({ me, licensorSchoolId, resource })
}

/**
 * Async version for route middleware (e.g. legacy library routes). Use client.query
 * so the full CFP check runs without React/hooks. Prefer useCanViewCoverPages() in components.
 */
export async function getCanViewCoverPages(
  pwContext: PathwrightContext
): Promise<boolean> {
  if (isCoverPagesFeatureEnabled(pwContext)) return true

  const client = getApolloClient()
  const result = await client.query<
    ContextualFeaturePermissionQuery,
    ContextualFeaturePermissionQueryVariables
  >({
    query: ContextualFeaturePermissionDocument,
    variables: coverPagesCfpVariables
  })

  const cfpPerm =
    result.data?.contextualFeaturePermission?.permission_in_context
  return isCoverPagesLaunched(cfpPerm)
}

export const useCoverPagesFeaturePermissionQuery = () => {
  return useContextualFeaturePermissionQuery({
    variables: coverPagesCfpVariables
  })
}

export const useCanViewCoverPages = () => {
  const pwContext = usePathwrightContext()

  // School feature override: when true, cover pages are on for the whole school (no CFP needed).
  if (isCoverPagesFeatureEnabled(pwContext)) return true

  const featurePermQuery = useCoverPagesFeaturePermissionQuery()
  const cfpPerm =
    featurePermQuery.data?.contextualFeaturePermission?.permission_in_context

  return isCoverPagesLaunched(cfpPerm)
}

// Preview-only access used by AboutCoverPageButton.
// In SchoolStaffOnly mode, this enables the About -> Cover preview path
// for admins/library editors while keeping all other cover-page UI paths disabled.
export const useCanPreviewCoverPagesFromAbout = () => {
  const pwContext = usePathwrightContext()

  // Fully launched feature should use normal cover-page routing/links.
  if (isCoverPagesFeatureEnabled(pwContext)) return false

  const featurePermQuery = useCoverPagesFeaturePermissionQuery()
  const cfpPerm =
    featurePermQuery.data?.contextualFeaturePermission?.permission_in_context

  return (
    cfpPerm === FeaturePermission.SchoolStaffOnly &&
    canStaffPreviewFromAbout(pwContext)
  )
}
